import { useMemo } from "react";
import TspApiService from "../../api/tspApiService";
import useTspApiResource from "../../hooks/useTspApiResource";
import useTspPagedList from "../../hooks/useTspPagedList";
import { ADVICE_PAGE_SIZE } from "./adviceModel";

const EMPTY_COUNTS = { all: 0, best_areas: 0, mistakes: 0, challenges: 0, suggestions: 0, future_opportunities: 0 };
const NOTHING = () => Promise.resolve({ data: null });

// The Advice Repository list: filters, category counts and paging are all done
// by the server. filters: { search, journeyId, locationId, category }
export function useTspAdviceList(filters) {
  const params = useMemo(
    () => ({
      q: filters.search.trim() || undefined,
      journey_id: filters.journeyId || undefined,
      location_id: filters.locationId || undefined,
      category: filters.category || undefined,
    }),
    [filters.search, filters.journeyId, filters.locationId, filters.category]
  );
  const list = useTspPagedList((page) => TspApiService.advice.list({ ...params, page, limit: ADVICE_PAGE_SIZE }), JSON.stringify(params));
  return { ...list, counts: list.meta?.counts || EMPTY_COUNTS };
}

// One advice record, plus the other advice recorded for the same city.
// `adviceId` undefined -> nothing to load (the "new" form).
export function useTspAdviceItem(adviceId) {
  const item = useTspApiResource(() => (adviceId ? TspApiService.advice.get(adviceId) : NOTHING()), [adviceId]);
  const locationId = item.data?.location?.id;
  const related = useTspApiResource(() => (locationId ? TspApiService.advice.list({ location_id: locationId, limit: 6 }) : NOTHING()), [locationId]);
  const others = useMemo(() => (related.data || []).filter((entry) => entry.id !== item.data?.id).slice(0, 4), [related.data, item.data]);
  return { item: item.data, loading: item.loading, error: item.error, reload: item.reload, related: others };
}

// The cities of one journey's route (the only places advice may be filed for).
export function useTspJourneyCities(journeyId) {
  const route = useTspApiResource(() => (journeyId ? TspApiService.getRoute(journeyId) : NOTHING()), [journeyId]);
  const cities = (route.data?.cities || []).map((city) => ({ locationId: city.location_id, label: city.label || city.city_name }));
  return { cities, loading: route.loading, error: route.error };
}

// Mutations. Each throws the axios error on refusal so the caller can show the
// server's own reason (see tspErrorMessage).
export const createAdvice = (body) => TspApiService.advice.create(body).then((response) => response.data);
export const updateAdvice = (id, body) => TspApiService.advice.update(id, body).then((response) => response.data);
export const removeAdvice = (id) => TspApiService.advice.remove(id);
